import fields from './fields';

// Per-customer field visibility for edit drawers (editProduct/editConvention).
// Config lives on the customer as Mixed maps (customer.productItems /
// customer.conventionItems), mirroring the journal pattern
// (customer.journalItems): key = field name from fields.js, value 1 = hidden,
// 0/absent = visible. Hiding is display-only; values are never deleted, and
// un-hiding writes 0 rather than removing the key.

const itemsKey = entityType => `${entityType}Items`;

// Blacklist for FormFieldsCompact's hideFields prop.
const getHideFields = (customer = {}, entityType) => {
    const items = (customer || {})[itemsKey(entityType)] || {};
    return Object.keys(items)
        .filter(key => items[key] === 1)
        .map(key => ({ name: key }));
};

// Flat list of fields a customer admin may toggle. Admin-only fields are
// already gated by hideAdmin, and dotted names would nest inside the Mixed
// map, so both are excluded.
const getConfigurableFields = (entityType) => {
    const sections = fields.get(entityType) || [];
    const seen = new Set();
    const result = [];
    sections.forEach(section => {
        (section.fields || []).forEach(field => {
            if (!field.name || field.isAdminField || field.name.includes('.') || seen.has(field.name)) {
                return;
            }
            seen.add(field.name);
            result.push({
                name: field.name,
                title: field.title,
                sectionKey: section.key,
                sectionTitle: section.name,
                isRecommended: !!field.isRecommended,
                isRequired: !!field.isRequired,
            });
        });
    });
    return result;
};

// One PATCH body applying the recommended set: hide everything that is
// neither recommended nor required. Entities without any recommended fields
// yield a no-op body so "apply" can never blank a whole form.
const buildApplyRecommendedPatch = (entityType) => {
    const list = getConfigurableFields(entityType);
    if (!list.some(field => field.isRecommended)) {
        return {};
    }
    const key = itemsKey(entityType);
    const body = {};
    list.forEach(field => {
        body[`${key}.${field.name}`] = (!field.isRecommended && !field.isRequired) ? 1 : 0;
    });
    return body;
};

// One PATCH body making every currently hidden field visible again.
const buildResetPatch = (customer = {}, entityType) => {
    const key = itemsKey(entityType);
    const items = (customer || {})[key] || {};
    const body = {};
    Object.keys(items).forEach(name => {
        if (items[name] === 1) {
            body[`${key}.${name}`] = 0;
        }
    });
    return body;
};

// Merge a dotted PATCH body into a local items map, for mirroring saved
// changes into userStore.customer so open drawers update live.
const applyPatchToItems = (items = {}, patchBody = {}, entityType) => {
    const prefix = `${itemsKey(entityType)}.`;
    const result = { ...items };
    Object.keys(patchBody).forEach(key => {
        if (key.startsWith(prefix)) {
            result[key.slice(prefix.length)] = patchBody[key];
        }
    });
    return result;
};

export {
    itemsKey,
    getHideFields,
    getConfigurableFields,
    buildApplyRecommendedPatch,
    buildResetPatch,
    applyPatchToItems,
};
